import * as React from 'react'
import styles from '../../data.grid.styl'
import cx from 'classnames'
import EditableCell from './cell.editable'
import { ColumnProps } from '../../models'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { CSSSelector } from '@src/utils/css.selector.helper'

export interface ExpanderTemplateProps<T> {
  className?: string
  isExpanded: boolean
  data: T
  column: ColumnProps<T>
  disabled: boolean
  onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}
export function ExpanderTemplate<T>({
  role,
  data,
  column,
  isExpanded,
  disabled,
  onClick,
  className
}: ExpanderTemplateProps<T> &
  React.HTMLAttributes<HTMLDivElement>): React.ReactElement<
  ExpanderTemplateProps<T>
> {
  return (
    <div
      role={role}
      className={cx(className, styles.expanderWrapper, { disabled })}
      onClick={onClick}
    >
      {column.expanderOptions.expanderTemplate(
        { original: data, isExpanded },
        column
      )}
    </div>
  )
}

export interface ClickableTemplateProps {
  column: ColumnProps<any>
  viewIndex: number
  onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}

export const ClickableTemplate: React.FC<ClickableTemplateProps> = ({
  column,
  viewIndex,
  children,
  onClick
}: React.PropsWithChildren<ClickableTemplateProps>) => (
  <div
    data-row={viewIndex}
    data-column={column?.field}
    onClick={onClick}
    className={cx('celllink', styles.linked)}
  >
    {children}
  </div>
)

export interface CellProps<T> {
  viewIndex: number
  isExpanded: boolean
  data: T
  value: any
  column: ColumnProps<T>
  defaultValue?: any
  onExpandRow: (viewIndex: number, field: keyof T) => void
}

const CellCSSSelectors = {
  expanderSelector: new CSSSelector('expanderInCell', 'div')
}

function Cell<T>(props: CellProps<T>) {
  const { viewIndex, data, column, isExpanded, defaultValue = '' } = props

  let { value } = props

  const history = useHistory()
  const match = useRouteMatch()

  function isEditable<T>(data: T, column: ColumnProps<T>) {
    const { editable } = column
    if (typeof editable === 'function') {
      return editable(data, column)
    }
    return editable
  }

  function isExpander<T>(column: ColumnProps<T>) {
    return column.expander && column.expanderOptions
  }
  function isClickable<T>(data: T, column: ColumnProps<T>) {
    return column.clickable ? column.clickable(data) : true
  }

  function isDisabled<T>(data: T, column: ColumnProps<T>) {
    return typeof column.disabled === 'function'
      ? column?.disabled(data)
      : column?.disabled
  }

  function onExpandRow() {
    const { onExpandRow } = props
    if (typeof onExpandRow === 'function' && isClickable(data, column)) {
      onExpandRow(viewIndex, column.field)
    }
  }

  function onClick(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    event.preventDefault()
    event.stopPropagation()
    if (typeof column.onClick === 'function') {
      column.onClick(data, column, history, match)
    }
  }

  if (isExpander(column)) {
    return (
      <ExpanderTemplate
        role={CellCSSSelectors.expanderSelector.value}
        isExpanded={isExpanded}
        data={data}
        disabled={isDisabled(data, column)}
        column={column}
        className={cx({ [styles.clickable]: isClickable(data, column) })}
        onClick={onExpandRow}
      />
    )
  }

  if (Array.isArray(value)) {
    value = value.join(', ')
  }

  const isColumnClickable = column.onClick && isClickable(data, column)

  return isColumnClickable ? (
    <ClickableTemplate column={column} viewIndex={viewIndex} onClick={onClick}>
      {value || defaultValue}
    </ClickableTemplate>
  ) : isEditable(data, column) ? (
    <EditableCell value={value} data={data} column={column} />
  ) : (
    value || defaultValue
  )
}

export default React.memo(Cell)
